<script setup lang="ts">
import BaseSpinner from '@/core/components/base/spinner/BaseSpinner.vue';
import {loyaltyCardApi} from '@/container';
import BaseCard from '@/core/components/base/BaseCard.vue';
import dateHelper from '@/core/helpers/dateHelper';
import BaseCardBody from '@/core/components/base/BaseCardBody.vue';
import BaseCardHeader from '@/core/components/base/BaseCardHeader.vue';
import BaseButton from '@/core/components/base/BaseButton.vue';
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";
import {inject, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {LoyaltyCardAnimation} from "@/loyalty-card/interfaces/LoyaltyCardAnimation";

const isLoading = ref<boolean>(false);
const animations = ref<any[]>([]);
const {environment} = useSystemStore();
const toast = useToastStore();
const route = useRoute();
const router = useRouter();

/** Injection of open confirmation modal function. */
const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

/**
 * Read the animations of loyalty card.
 *
 * @param id - The loyalty card id.
 * @return {Promise<Array>}
 */
const readAnimations = (id: number) => {
    if (environment?.entityId) {
        isLoading.value = true;
        return loyaltyCardApi()
            .animations(environment.entityId, id)
            .then(response => {
                animations.value = response;
            })
            .finally(() => {
                isLoading.value = false;
            });
    }
};


watch(() => route.query.loyaltyCardId, () => {
    readAnimations(Number(route.query.loyaltyCardId));
}, {immediate: true});

/**
 * Action when see sales button is pressed.
 *
 * @param animation
 */
const onPress = (animation: LoyaltyCardAnimation) => {
    router.push({
        name: 'program.detail.animations.transactions',
        params: {animationId: animation.id}
    });
};

/**
 * Delete a loyalty card animation and remove it from the list.
 */
const deleteAnimation = (animation: LoyaltyCardAnimation) => {
    if (!environment?.entityId) {
        return Promise.resolve();
    }

    return loyaltyCardApi()
        .deleteAnimation(environment.entityId, Number(route.query.loyaltyCardId), animation.id)
        .then(() => {
            animations.value = animations.value.filter(item => item.id !== animation.id);
            toast.showSuccess({content: "L'animation a été supprimée."});
        })
        .catch(() => {
            toast.showError({content: "La suppression de l'animation a échoué."});
        });
};

/**
 * Ask for confirmation before deleting an animation.
 *
 * @param animation
 */
const confirmDelete = (animation: LoyaltyCardAnimation) => {
    openConfirmationModal(
        "Supprimer l'animation",
        `Êtes-vous sûr de vouloir supprimer l'animation « ${animation.name} » ? Cette action est irréversible.`,
        () => deleteAnimation(animation) as Promise<void>
    );
};

/**
 * Redirect to the page that allow the creation of a loyalty card animation.
 */
const redirectToCreate = () => {
    router.push({
        name: 'program.detail.animations.create',
        query: {loyaltyCardId: route.query.loyaltyCardId}
    });
};

</script>

<template>
  <div v-if="isLoading">
    <BaseSpinner />
  </div>
  <div
    v-else
    class="mx-6"
  >
    <div v-if="!!animations?.length">
      <div class="flex flex-row items-center justify-between pt-2">
        <div>
          <h2>Animations</h2>
        </div>
        <BaseButton primary @click="redirectToCreate">
          Créer une nouvelle animation
        </BaseButton>
      </div>
      <BaseCard
        v-for="animation in animations"
        :key="animation.id"
        class="relative overflow-visible my-5"
      >
        <BaseCardHeader>
          <div class="flex justify-between">
            <p>{{ animation.name }}</p>
            <p class="text-sm">
              Du {{ dateHelper.shortFormat(animation.start_date) }} au {{ dateHelper.shortFormat(animation.end_date) }}
            </p>
          </div>
        </BaseCardHeader>
        <BaseCardBody>
          <div class="flex justify-between align-center">
            <div>
              <p>{{ animation.description }}</p>
              <p class="text-primary">
                x{{ animation.coefficient }} appliqué aux points crédités.
              </p>
            </div>
            <div class="flex items-center gap-2">
              <BaseButton
                v-if="animation.status !== 'incoming'"
                primary
                class="justify-center"
                @click="onPress(animation)"
              >
                Voir les ventes
              </BaseButton>
              <BaseButton
                prepend-icon="mdi-delete"
                color="error"
                class="justify-center"
                @click="confirmDelete(animation)"
              >
                Supprimer
              </BaseButton>
            </div>
          </div>
        </BaseCardBody>
      </BaseCard>
    </div>
    <div
      v-else
      class="flex flex-col gap-4 py-2 align-center justify-center"
    >
      <h2>
        Boostez votre programme de fidélité avec les animations !
        Proposez à vos patients des périodes où leurs points sont multipliés, et prévenez-les via notre
        module Notifications.
      </h2>
      <p>Aucune animations pour cette carte de fidélité.</p>
      <BaseButton primary @click="redirectToCreate">
        Créer une nouvelle animation
      </BaseButton>
    </div>
  </div>
</template>

